import { Input, Modal, message } from 'antd';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { userConfigStore } from '@/stores';

const { TextArea } = Input;

interface CreateDefinitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

export function CreateDefinitionDialog({
  open,
  onOpenChange,
}: CreateDefinitionDialogProps) {
  const [configKey, setConfigKey] = useState('');
  const [jsonValue, setJsonValue] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [jsonError, setJsonError] = useState('');

  const resetForm = () => {
    setConfigKey('');
    setJsonValue('');
    setDescription('');
    setJsonError('');
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  const handleJsonChange = (value: string) => {
    setJsonValue(value);
    if (value && !isValidJson(value)) {
      setJsonError('请输入合法的 JSON 格式');
    } else {
      setJsonError('');
    }
  };

  const isSubmitDisabled =
    loading || !configKey.trim() || !jsonValue.trim() || !!jsonError;

  const handleSubmit = async () => {
    if (!configKey.trim() || !jsonValue.trim()) return;
    if (!isValidJson(jsonValue)) {
      setJsonError('请输入合法的 JSON 格式');
      return;
    }

    setLoading(true);
    try {
      await userConfigStore.createDefinition({
        config_key: configKey.trim(),
        json_value: jsonValue.trim(),
        description: description.trim(),
      });
      message.success('配置创建成功');
      handleClose();
    } catch (error) {
      console.error('创建失败:', error);
      message.error('创建失败，配置键可能已存在');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="创建配置定义"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            {loading ? '创建中...' : '创建'}
          </Button>
        </div>
      }
      centered
      width={560}
      styles={{
        container: {
          padding: '24px',
          borderRadius: '8px',
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
        },
      }}
    >
      <div className="space-y-4 pt-2">
        <div className="space-y-2">
          <Label htmlFor="configKey">配置键 *</Label>
          <Input
            id="configKey"
            value={configKey}
            onChange={(e) => setConfigKey(e.target.value)}
            placeholder="例如：internal_user, beta_feature_x"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            建议使用小写字母和下划线，例如 internal_user
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">描述</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="配置用途说明"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="jsonValue">JSON 值 *</Label>
          <TextArea
            id="jsonValue"
            value={jsonValue}
            onChange={(e) => handleJsonChange(e.target.value)}
            placeholder={'例如：true\n或：{"enabled": true, "level": 1}'}
            rows={4}
            className="font-mono text-sm"
            status={jsonError ? 'error' : undefined}
          />
          {jsonError ? (
            <p className="text-xs text-red-500">{jsonError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              必须是合法的 JSON，例如 true、1、"text"、{'{}'} 或 []
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
